/* AppJsonPage — the whole app as ONE editable document, in-shell. On mount it
 * exports this app's bundle ({ app, sources[], workflows[], journeys[],
 * roles[] }), pretty-prints it into a CodeMirror JSON editor; Save re-imports
 * the edited bundle. bundle.app.id is FORCED to this appId on save so the paste
 * always UPSERTS this app (never mints a new one). Renders inside the shell's
 * <Outlet/> (no aps-root/org-bar chrome — the shell provides it). Mirrors
 * ImportAppScreen's editor / Valid-JSON chip / saved-pills / errors look. */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    Download,
    FileJson,
    Loader2,
    RefreshCw,
    RotateCcw,
    Save,
    Upload
} from 'lucide-react';
import {
    appEngineService,
    describeError
} from '../api/appEngine.service';
import CodeMirrorJson from '../shared/CodeMirrorJson';
import {
    useAppStudio
} from '../AppStudioContext';
import {
    notify
} from 'shared/api/notifier';

type ImportResult = {
    mode?: string;
    app_id?: string;
    saved?: string[];
    errors?: Record<string, string[]>;
    warnings?: Record<string, string[]>;
};

/** Flatten a Record<piece, messages[]> into ordered [piece, message] rows. */
function flatten(map: Record<string, string[]> | undefined): Array<[string, string]> {
    const rows: Array<[string, string]> = [];
    if (!map || typeof map !== 'object') return rows;
    for (const [piece, msgs] of Object.entries(map)) {
        const arr = Array.isArray(msgs) ? msgs : [String(msgs)];
        arr.forEach((m) => rows.push([piece, String(m)]));
    }
    return rows;
}

const CARD: React.CSSProperties = {
    background: 'var(--aps-surface)',
    border: '1px solid var(--aps-border)',
    borderRadius: 'var(--aps-radius-md)'
};

const GREEN_PILL: React.CSSProperties = {
    fontSize: 'var(--aps-text-xs)',
    padding: '4px 10px',
    borderRadius: 999,
    background: 'color-mix(in srgb, var(--aps-success) 12%, transparent)',
    color: 'var(--aps-success)',
    border: '1px solid color-mix(in srgb, var(--aps-success) 30%, transparent)'
};

export default function AppJsonPage() {
    const {
        orgId,
        appId
    } = useAppStudio();

    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    // Fetch the whole-app bundle and pretty-print it into the editor.
    const fetchExport = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        setResult(null);
        try {
            const bundle = await appEngineService.exportApp(orgId, appId);
            setText(JSON.stringify(bundle, null, 2));
        } catch (err) {
            setLoadError(describeError(err));
        } finally {
            setLoading(false);
        }
    }, [orgId, appId]);

    useEffect(() => {
        fetchExport();
    }, [fetchExport]);

    // Live parse gate — Save stays dead until the doc is valid JSON. Flat shape
    // (this project compiles with strictNullChecks off, where union-discriminant
    // narrowing is unreliable).
    const parsed = useMemo<{ ok: boolean; value?: any; error?: string }>(() => {
        try {
            return { ok: true, value: JSON.parse(text) };
        } catch (e: any) {
            return { ok: false, error: String(e?.message || 'invalid JSON') };
        }
    }, [text]);

    const errorEntries = result && result.errors ? Object.keys(result.errors) : [];
    const hasErrors = errorEntries.length > 0;
    const success = !!result && !hasErrors;
    const errorRows = hasErrors ? flatten(result?.errors) : [];
    const warningRows = flatten(result?.warnings);
    const saved = Array.isArray(result?.saved) ? (result?.saved as string[]) : [];

    // Load a bundle from disk into the editor (normal paste also works).
    const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';                 // allow re-selecting the same file
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setText(String(reader.result || ''));
            setResult(null);
        };
        reader.onerror = () => notify('Could not read that file', 'danger');
        reader.readAsText(file);
    };

    // Download the current editor text as <appId>.json + copy to clipboard.
    const onExport = () => {
        try {
            const blob = new Blob([text], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${appId}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text).catch(() => undefined);
            }
            notify('Exported app JSON', 'success');
        } catch (err) {
            notify(describeError(err), 'danger');
        }
    };

    // Discard edits and re-fetch the export.
    const onReset = () => {
        if (!window.confirm('Discard your edits and reload the app JSON from the server?')) {
            return;
        }
        fetchExport();
    };

    const onSave = async () => {
        if (busy || !parsed.ok) return;
        // FORCE the bundle to target THIS app so a save always upserts it,
        // never mints a new app.
        const bundle: any = { ...(parsed.value || {}) };
        bundle.app = { ...(bundle.app || {}), id: appId };
        setBusy(true);
        try {
            const res: ImportResult = await appEngineService.importApp(orgId, bundle);
            setResult(res || {});
            const errKeys = res && res.errors ? Object.keys(res.errors) : [];
            if (errKeys.length === 0) {
                notify('Saved — every piece upserted into this app', 'success');
            } else {
                notify(`${errKeys.length} item(s) failed — see the report`, 'warning');
            }
        } catch (err) {
            notify(describeError(err), 'danger');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '20px 20px 24px'
        }}>
            {/* header row — title + subtitle, live Valid/Invalid-JSON chip right */}
            <div className="aps-page-head" style={{ marginBottom: 14, alignItems: 'flex-start' }}>
                <div className="aps-page-head-text">
                    <h1 className="aps-h2">App JSON</h1>
                    <p className="aps-page-sub">
                        The whole app as one document — edit it and Save changes;
                        every piece is validated and upserted.
                    </p>
                </div>
                {!loading && !loadError && (
                    <div className="aps-page-head-actions">
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: 'var(--aps-text-xs)', fontWeight: 700,
                            padding: '3px 10px', borderRadius: 999,
                            color: parsed.ok ? 'var(--aps-success)' : 'var(--aps-danger)',
                            background: parsed.ok
                                ? 'color-mix(in srgb, var(--aps-success) 12%, transparent)'
                                : 'color-mix(in srgb, var(--aps-danger) 12%, transparent)'
                        }}>
                            <span style={{
                                width: 7, height: 7, borderRadius: '50%',
                                background: 'currentColor'
                            }} />
                            {parsed.ok ? 'Valid JSON' : 'Invalid JSON'}
                        </span>
                    </div>
                )}
            </div>

            {loading ? (
                /* ── loading ──────────────────────────────────────────────── */
                <div style={{
                    ...CARD, padding: '40px', display: 'flex', gap: 10,
                    alignItems: 'center', justifyContent: 'center',
                    color: 'var(--aps-text-muted)'
                }}>
                    <Loader2 size={18} className="aps-spin" color="var(--aps-primary)" />
                    Loading app JSON…
                </div>
            ) : loadError ? (
                /* ── fetch error ──────────────────────────────────────────── */
                <div style={{ ...CARD, padding: '20px 22px', borderColor: 'var(--aps-danger)' }}>
                    <p style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        margin: '0 0 6px', fontWeight: 700, color: 'var(--aps-text)'
                    }}>
                        <AlertTriangle size={16} color="var(--aps-danger)" />
                        Couldn't load the app JSON
                    </p>
                    <p className="aps-help" style={{ margin: '0 0 12px' }}>{loadError}</p>
                    <button className="aps-btn aps-btn-secondary" onClick={fetchExport}>
                        <RefreshCw size={14} /> Retry
                    </button>
                </div>
            ) : (
                /* ── editor ───────────────────────────────────────────────── */
                <>
                    {/* toolbar */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12
                    }}>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".json,application/json"
                            onChange={onPickFile}
                            style={{ display: 'none' }}
                        />
                        <button className="aps-btn aps-btn-secondary aps-btn-sm"
                            title="Load a bundle from disk"
                            onClick={() => fileRef.current?.click()}>
                            <Upload size={14} /> Import file
                        </button>
                        <button className="aps-btn aps-btn-secondary aps-btn-sm"
                            title="Download the current JSON (and copy to clipboard)"
                            onClick={onExport}>
                            <Download size={14} /> Export
                        </button>
                        <button className="aps-btn aps-btn-secondary aps-btn-sm"
                            title="Discard edits and reload from the server"
                            onClick={onReset}>
                            <RotateCcw size={14} /> Reset
                        </button>
                        <span style={{ flex: 1 }} />
                        <button className="aps-btn aps-btn-primary aps-btn-sm"
                            disabled={busy || !parsed.ok} onClick={onSave}>
                            {busy
                                ? <Loader2 size={14} className="aps-spin" />
                                : <Save size={14} />}
                            {busy ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>

                    {/* the whole-app document */}
                    <div style={{ ...CARD, overflow: 'hidden' }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '12px 16px', borderBottom: '1px solid var(--aps-border)'
                        }}>
                            <FileJson size={16} color="var(--aps-primary)" />
                            <span style={{ fontWeight: 700, color: 'var(--aps-text)' }}>
                                App bundle
                            </span>
                            <span style={{ flex: 1 }} />
                            <span className="aps-mono" style={{
                                fontSize: 'var(--aps-text-xs)', color: 'var(--aps-text-muted)'
                            }}>{appId}</span>
                        </div>
                        <div className="aps-editor-frame" style={{
                            height: '60vh', minHeight: 360, flex: 'none',
                            border: 'none', borderRadius: 0
                        }}>
                            <CodeMirrorJson value={text} onChange={setText} />
                        </div>
                        {!parsed.ok && (
                            <div className="aps-errors" style={{ margin: 12 }}>
                                <p className="aps-errors-title">Not valid JSON yet</p>
                                <ul><li>{parsed.error}</li></ul>
                            </div>
                        )}
                    </div>

                    {/* result — saved pills / per-piece errors / warnings */}
                    {result && (
                        <div style={{
                            marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12
                        }}>
                            {success && (
                                <div style={{
                                    ...CARD, padding: '12px 16px',
                                    borderColor: 'var(--aps-success)',
                                    display: 'flex', alignItems: 'center', gap: 8
                                }}>
                                    <CheckCircle2 size={16} color="var(--aps-success)" />
                                    <span style={{ fontWeight: 700, color: 'var(--aps-text)' }}>
                                        Saved — every piece upserted into this app.
                                    </span>
                                </div>
                            )}
                            {saved.length > 0 && (
                                <div>
                                    <p className="aps-help" style={{ margin: '0 0 6px' }}>
                                        {hasErrors ? 'Saved anyway' : 'Saved'}
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {saved.map((s, i) => (
                                            <span key={i} className="aps-mono" style={GREEN_PILL}>{s}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {hasErrors && (
                                <div className="aps-errors">
                                    <p className="aps-errors-title">
                                        {errorEntries.length} item(s) failed — fix and re-save
                                    </p>
                                    <ul>
                                        {errorRows.map(([piece, msg], i) => (
                                            <li key={i}>
                                                <span className="aps-mono">{piece}</span>: {msg}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {warningRows.length > 0 && (
                                <div className="aps-warnings">
                                    <p className="aps-errors-title" style={{
                                        display: 'flex', alignItems: 'center', gap: 6
                                    }}>
                                        <AlertTriangle size={13} color="var(--aps-warning)" />
                                        Warnings ({warningRows.length})
                                    </p>
                                    <ul>
                                        {warningRows.map(([piece, msg], i) => (
                                            <li key={i}>
                                                <span className="aps-mono">{piece}</span>: {msg}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
